// screens/RDWorkspaceScreen.js - Screen 11
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncBar, EmptyState, useSmartBack } from '../components/shared';
import api from '../utils/api';

export default function RDWorkspaceScreen() {
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newRef, setNewRef] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => { loadProjects(); }, []);

  useEffect(() => {
    const t = setTimeout(() => loadProjects(), 300);
    return () => clearTimeout(t);
  }, [q]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const params = q ? { q } : {};
      const { data } = await api.get('/api/rd/projects', { params });
      setProjects(data);
    } catch {} finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newRef.trim() || !newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post('/api/rd/projects', { reference: newRef.trim(), name: newName.trim() });
      navigate(`/rd/${data.id}`);
    } catch {
      alert('Failed to create project');
    } finally { setCreating(false); }
  };

  const STAGE_COLORS = {
    Concept: 'var(--accent-secondary)',
    Prototype: 'var(--accent-primary)',
    Sample: 'var(--accent-info)',
    'Pre-production': 'var(--accent-warning)',
    Final: 'var(--accent-success)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={goBack}>←</button>
        <h2>R&D Workspace</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ Project</button>
      </div>

      <div className="page-content">
        {/* Search */}
        <div className="search-wrap" style={{ marginBottom: 16 }}>
          <div className="search-icon">🔍</div>
          <input type="search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by reference or name..." className="input-field" />
        </div>

        {/* Switch back */}
        <div style={{ marginBottom: 20 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/')}>
            ← Switch to Production
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div className="spinner spinner-lg" />
          </div>
        ) : projects.length === 0 ? (
          <EmptyState
            icon="🔬"
            title="No R&D Projects"
            message="Create your first R&D project to get started"
            action={<button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Project</button>}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {projects.map(p => (
              <div key={p.id} className="card card-hover" style={{ padding: 16 }}
                onClick={() => navigate(`/rd/${p.id}`)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent-secondary)', fontSize: '0.9375rem' }}>
                        {p.reference}
                      </span>
                      <span className={`badge ${p.status === 'open' ? 'badge-success' : 'badge-secondary'}`}>
                        {p.status}
                      </span>
                      {p.site_name && <span className="site-badge">{p.site_name}</span>}
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {p.photo_count} photos · by {p.creator_name} · {new Date(p.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ color: 'var(--text-muted)' }}>›</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New project modal */}
      {showNew && (
        <div className="overlay" onClick={() => setShowNew(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 20 }}>New R&D Project</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Project Reference *</label>
                <input type="text" value={newRef} onChange={e => setNewRef(e.target.value)}
                  className="input-field" placeholder="e.g. RD-2024-001 or Project Alpha"
                  autoFocus />
                <div className="form-hint">Free-form project number or name</div>
              </div>
              <div className="form-group">
                <label className="form-label">Project Name *</label>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                  className="input-field" placeholder="Descriptive project name"
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary btn-full" onClick={() => setShowNew(false)}>Cancel</button>
                <button className="btn btn-primary btn-full" onClick={handleCreate}
                  disabled={!newRef.trim() || !newName.trim() || creating}>
                  {creating ? 'Creating...' : 'Create Project →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
